import React, { useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import BottomSheet from './ui/BottomSheet';
import { buildRiderAppStaticQrUrl } from '../utils/riderAppPaymentUrl';

/**
 * Static Driver / Vehicle QR (DRIVERAPP_HOSTED_PAGE_PAYMENTS.md §"Update DriverQrSheet").
 *
 * A lookup code, not a payment link: it encodes `https://<fleet>.riderapp.net/pay`
 * with only `vehicleId` (sticker in the passenger area) or, without a vehicle,
 * `driverId` (business card). RiderApp finds the active trip serviced by that unit
 * — or opens New Trip pinned to it when there is none — and owns fare/tip and the
 * HBSS hosted page from there. Nothing here talks to the server; in particular no
 * `requestTripCollection` belongs in this sheet.
 */
function DriverQrSheet({ open, onClose, driverId, vehicleId, fleetCode, riderAppUrl }) {
  const qr = useMemo(
    () => buildRiderAppStaticQrUrl({ fleetCode, riderAppUrl, vehicleId, driverId }),
    [fleetCode, riderAppUrl, vehicleId, driverId]
  );
  const missingFleet = !fleetCode && !riderAppUrl;

  return (
    <BottomSheet open={open} onClose={onClose} title="Driver QR Code">
      <div className="flex flex-col items-center gap-4 py-4">
        {missingFleet && (
          <p className="text-center text-sm text-gray-500">
            The RiderApp address for this fleet is not known yet (no fleet code). Sign in again
            to load fleet settings.
          </p>
        )}
        {!missingFleet && !qr && (
          <p className="text-center text-sm text-gray-500">
            Select a vehicle or sign in as a driver to generate your QR code.
          </p>
        )}
        {qr && (
          <>
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <QRCodeSVG value={qr.url} size={220} />
            </div>
            <p className="text-center text-sm font-semibold text-gray-800">
              Scan to find and pay an active trip
            </p>
            <p className="text-center text-xs text-gray-500">
              {qr.kind === 'vehicle'
                ? 'Vehicle code — passengers in this vehicle are matched to its current trip.'
                : 'Driver code — passengers are matched to the trip you are currently driving.'}
              {' '}With no active trip, the RiderApp offers a new booking with this unit.
            </p>
            <code
              data-testid="driver-qr-url"
              className="block w-full break-all rounded-lg bg-gray-100 px-3 py-2 text-center text-[11px] text-gray-600"
            >
              {qr.url}
            </code>
          </>
        )}
      </div>
    </BottomSheet>
  );
}

export default DriverQrSheet;
